import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import decodeXml from "./decode";
import { XmlParseError, parseXmlDocument } from "./dom";
import parseInvoice from "./parseInvoice";
import { XmlFormat, XmlInvoice } from "./types";

class XmlFetchError extends Error {
    status: number;

    constructor(status: number) {
        super(`XML document request failed with status ${status}`);
        this.name = "XmlFetchError";
        this.status = status;
    }
}

export type XmlDocumentState =
    | { status: "loading" }
    | { status: "failed" }
    | { status: "unreadable" }
    | { status: "loaded"; invoice: XmlInvoice };

interface Options {
    file: string | null;
    format: XmlFormat;
    accountId: number | null;
    documentId: number | null;
}

type Report = (error: unknown) => void;

function readInvoice(text: string, format: XmlFormat, report: Report): XmlInvoice | null {
    try {
        return parseInvoice(parseXmlDocument(text), format);
    } catch (error) {
        if (!(error instanceof XmlParseError)) report(error);
        return null;
    }
}

export default function useXmlDocument({
    file,
    format,
    accountId,
    documentId,
}: Options): XmlDocumentState {
    const [state, setState] = useState<XmlDocumentState>({ status: "loading" });

    useEffect(() => {
        if (!file) {
            setState({ status: "failed" });
            return undefined;
        }

        const controller = new AbortController();
        const report: Report = (error) =>
            Sentry.captureException(error, {
                tags: { accountId: String(accountId), documentId: String(documentId), format },
            });

        setState({ status: "loading" });

        (async () => {
            try {
                const response = await fetch(file, { signal: controller.signal });
                if (!response.ok) throw new XmlFetchError(response.status);

                const text = decodeXml(await response.arrayBuffer());
                if (controller.signal.aborted) return;

                const invoice = readInvoice(text, format, report);
                setState(invoice ? { status: "loaded", invoice } : { status: "unreadable" });
            } catch (error) {
                if (controller.signal.aborted) return;
                if (!(error instanceof XmlFetchError && error.status < 500)) report(error);
                setState({ status: "failed" });
            }
        })();

        return () => controller.abort();
    }, [file, format, accountId, documentId]);

    return state;
}
